import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Download, FileText, Target, TrendingUp, Shield, Users, 
  DollarSign, BarChart3, ArrowRight, CheckCircle2, Zap,
  Building2, Store, Package, Globe, Printer
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const StrategyPage = () => {
  const printRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const useCases = [
    {
      icon: Shield,
      title: "Pricing Guardrails",
      subtitle: "Risk Prevention",
      problem: "Business owners make pricing mistakes that erode margins or trigger competitive retaliation.",
      solution: "Real-time alerts for pricing below cost, margin erosion warnings, and approval workflows.",
      value: "Prevent the $50K mistake before it happens",
      metrics: [
        "Average margin improvement: 3-8%",
        "Pricing errors prevented: 15-25/month",
        "Time saved: 10+ hours/week"
      ],
      color: "from-red-500 to-red-600"
    },
    {
      icon: BarChart3,
      title: "Price Simulation",
      subtitle: "What-If Analysis",
      problem: "Business owners don't know the impact of price changes before implementing them.",
      solution: "Interactive simulator showing volume impact, revenue/profit trade-offs, and demand curves.",
      value: "See the impact before you commit",
      metrics: [
        "Decision confidence increase: 60%",
        "Bad price changes avoided: 8-12/quarter",
        "ROI visibility: Same-day"
      ],
      color: "from-yellow-500 to-yellow-600"
    },
    {
      icon: Target,
      title: "Competitive Intelligence",
      subtitle: "Market Monitoring",
      problem: "Manual competitor tracking is time-consuming and incomplete.",
      solution: "Automated monitoring of competitor prices, market positioning, and opportunity alerts.",
      value: "Know your market position in real-time",
      metrics: [
        "Time saved: 20+ hours/month",
        "Response time: 24hrs → 2hrs",
        "Market coverage: 95%+ competitors"
      ],
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Zap,
      title: "AI Recommendations",
      subtitle: "Smart Suggestions",
      problem: "Pricing decisions are based on gut feeling, not data.",
      solution: "Explainable AI recommendations with confidence scores and risk assessment.",
      value: "Data-driven decisions with human control",
      metrics: [
        "Revenue lift: 5-15%",
        "Recommendation accuracy: 85%+",
        "Adoption rate: 70%+ reviewed"
      ],
      color: "from-purple-500 to-purple-600"
    }
  ];

  const industries = [
    { icon: Store, name: "E-commerce & Retail", example: "500-5,000 SKUs, $1-10M revenue" },
    { icon: Package, name: "Wholesale & Distribution", example: "1,000-25,000 SKUs, volume pricing" },
    { icon: Building2, name: "Manufacturing", example: "Dealer networks, B2B pricing" },
    { icon: Globe, name: "SaaS & Subscriptions", example: "Plan optimization, churn reduction" }
  ];

  const growthSignals = [
    {
      signal: "Your product catalog is growing faster than you can track",
      challenge: "Managing pricing across 500+ SKUs manually",
      howWeHelp: "Automated price monitoring catches margin leaks you'd miss. One customer found $12K in underpriced items in week one.",
      icon: Package
    },
    {
      signal: "You're losing deals but don't know if it's price or something else",
      challenge: "No visibility into competitive positioning",
      howWeHelp: "See exactly where you stand vs competitors. Know when you're overpriced (losing volume) or underpriced (leaving money on the table).",
      icon: Target
    },
    {
      signal: "Your team makes pricing decisions differently",
      challenge: "Inconsistent pricing across salespeople or locations",
      howWeHelp: "Set guardrails that prevent mistakes while giving your team flexibility. Everyone prices within safe bounds.",
      icon: Users
    },
    {
      signal: "A bad price change hurt you and you didn't see it coming",
      challenge: "No way to test price changes before going live",
      howWeHelp: "Simulate any price change and see projected impact on volume, revenue, and margin before you commit.",
      icon: Shield
    }
  ];

  const commonChallenges = [
    {
      challenge: "I don't have time to analyze pricing—I'm running a business.",
      reality: "Most SMB owners spend 5-10 hours/month on pricing, usually reactively. That's time spent fixing problems, not preventing them.",
      insight: "PriceStrike monitors your prices 24/7 and alerts you only when action is needed. Most users check in 15 minutes/week.",
      outcome: "Turn reactive firefighting into proactive protection"
    },
    {
      challenge: "My margins are fine. I don't have a pricing problem.",
      reality: "The average SMB has 15-20% of products priced wrong—either too high (losing sales) or too low (losing margin). You just can't see it yet.",
      insight: "Our free Pricing Health Check shows exactly where your risks are. No commitment, just clarity.",
      outcome: "Discover the margin you didn't know you were missing"
    },
    {
      challenge: "I know my business better than any software could.",
      reality: "You absolutely do. That's why PriceStrike doesn't replace your judgment—it extends it. You still make every decision.",
      insight: "Think of it as a co-pilot who watches the data you can't, and flags what needs your attention.",
      outcome: "Your expertise + data visibility = confident decisions"
    },
    {
      challenge: "I tried a pricing tool once. Too complicated, never used it.",
      reality: "Enterprise pricing tools are built for analysts with months of implementation. That's not you, and that's not us.",
      insight: "PriceStrike was built for business owners who wear multiple hats. If you can use Excel, you can use PriceStrike.",
      outcome: "See value in your first week, not your first quarter"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      <Header />
      
      {/* Print Styles */}
      <style>
        {`
          @media print {
            body { background: white !important; }
            .no-print { display: none !important; }
            .print-break { page-break-before: always; }
            .print-section { 
              background: white !important; 
              color: black !important;
              padding: 20px !important;
            }
            .print-section * { color: black !important; }
            .print-section h1, .print-section h2, .print-section h3 { color: #1e40af !important; }
          }
        `}
      </style>

      {/* Hero */}
      <section className="pt-40 pb-16 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827] no-print">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
                <FileText className="w-4 h-4 text-[#2563EB]" />
                <span className="text-[#2563EB] font-medium text-sm">SMB Pricing Guide</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#F8FAFC] mb-4">
                Pricing Risk &
                <span className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] bg-clip-text text-transparent"> How We Help</span>
              </h1>
              <p className="text-xl text-[#94A3B8] max-w-2xl">
                Understand where pricing risk hides in your business—and how PriceStrike helps you see it before it costs you.
              </p>
            </div>
            <div className="flex gap-4">
              <Button 
                onClick={handlePrint}
                className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-6 py-6"
              >
                <Printer className="w-5 h-5 mr-2" />
                Print / Save PDF
              </Button>
              <a href="/STRATEGY.md" download>
                <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 px-6 py-6">
                  <Download className="w-5 h-5 mr-2" />
                  Download Full Doc
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Core Use Cases */}
      <section ref={printRef} className="py-16 px-6 bg-[#0A0F1C] print-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">Core Use Cases</h2>
          <p className="text-[#94A3B8] mb-10">Four primary ways PriceStrike delivers value to customers.</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-2xl p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 bg-gradient-to-br ${useCase.color} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#F8FAFC]">{useCase.title}</h3>
                      <p className="text-sm text-[#94A3B8]">{useCase.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-red-400 uppercase mb-1">Problem</p>
                      <p className="text-[#94A3B8] text-sm">{useCase.problem}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-green-400 uppercase mb-1">Solution</p>
                      <p className="text-[#94A3B8] text-sm">{useCase.solution}</p>
                    </div>
                    <div className="bg-[#2563EB]/10 rounded-lg p-3">
                      <p className="text-xs font-semibold text-[#2563EB] uppercase mb-1">Value Proposition</p>
                      <p className="text-[#F8FAFC] font-medium">"{useCase.value}"</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[#94A3B8] uppercase mb-2">Key Metrics</p>
                      <ul className="space-y-1">
                        {useCase.metrics.map((metric, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-[#94A3B8]">
                            <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                            {metric}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Target Industries */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C] print-section print-break">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">Target Industries</h2>
          <p className="text-[#94A3B8] mb-10">Primary market segments with highest conversion potential.</p>
          
          <div className="grid md:grid-cols-4 gap-4">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div key={index} className="bg-[#111827] border border-[#2563EB]/20 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 bg-[#2563EB]/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-[#2563EB]" />
                  </div>
                  <h3 className="text-[#F8FAFC] font-bold mb-1">{industry.name}</h3>
                  <p className="text-xs text-[#94A3B8]">{industry.example}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Signs You Need Pricing Help */}
      <section className="py-16 px-6 bg-[#0A0F1C] print-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">Signs Your Business Needs Pricing Help</h2>
          <p className="text-[#94A3B8] mb-10">Recognize these signals? You're not alone—and there's a path forward.</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {growthSignals.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/20 rounded-xl p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-yellow-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-yellow-400 uppercase mb-1">Warning Sign</p>
                      <p className="text-[#F8FAFC] font-semibold">{item.signal}</p>
                    </div>
                  </div>
                  <div className="space-y-3 ml-16">
                    <div>
                      <p className="text-xs font-semibold text-red-400 uppercase mb-1">The Challenge</p>
                      <p className="text-[#94A3B8] text-sm">{item.challenge}</p>
                    </div>
                    <div className="bg-[#2563EB]/10 rounded-lg p-3">
                      <p className="text-xs font-semibold text-[#2563EB] uppercase mb-1">How PriceStrike Helps</p>
                      <p className="text-[#F8FAFC] text-sm">{item.howWeHelp}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Common Challenges */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#111827] to-[#0A0F1C] print-section print-break">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">What SMB Owners Tell Us</h2>
          <p className="text-[#94A3B8] mb-10">Common concerns we hear—and the reality behind them.</p>
          
          <div className="space-y-6">
            {commonChallenges.map((item, index) => (
              <div key={index} className="bg-[#111827] border border-[#2563EB]/20 rounded-xl p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-[#94A3B8]/20 text-[#94A3B8] text-xs font-bold px-2 py-1 rounded">WHAT YOU MIGHT THINK</span>
                    </div>
                    <p className="text-[#F8FAFC] font-medium text-lg mb-4">"{item.challenge}"</p>
                    <div>
                      <p className="text-xs font-semibold text-yellow-400 uppercase mb-1">The Reality</p>
                      <p className="text-[#94A3B8] text-sm">{item.reality}</p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-[#2563EB]/10 to-transparent rounded-lg p-5">
                    <p className="text-xs font-semibold text-[#2563EB] uppercase mb-2">What We've Learned</p>
                    <p className="text-[#F8FAFC] text-sm mb-4">{item.insight}</p>
                    <div className="flex items-center gap-2 pt-3 border-t border-[#2563EB]/20">
                      <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <p className="text-green-400 font-medium text-sm">{item.outcome}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Quick Reference */}
      <section className="py-16 px-6 bg-[#0A0F1C] print-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-[#F8FAFC] mb-2">Pricing Quick Reference</h2>
          <p className="text-[#94A3B8] mb-10">Tier comparison for quick reference during calls.</p>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2563EB]/30">
                  <th className="text-left py-4 px-4 text-[#F8FAFC] font-bold">Tier</th>
                  <th className="text-left py-4 px-4 text-[#F8FAFC] font-bold">Price</th>
                  <th className="text-left py-4 px-4 text-[#F8FAFC] font-bold">SKUs</th>
                  <th className="text-left py-4 px-4 text-[#F8FAFC] font-bold">Users</th>
                  <th className="text-left py-4 px-4 text-[#F8FAFC] font-bold">Best For</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-[#2563EB]/10">
                  <td className="py-4 px-4 text-[#94A3B8]">Freemium</td>
                  <td className="py-4 px-4 text-[#F8FAFC] font-semibold">$0</td>
                  <td className="py-4 px-4 text-[#94A3B8]">10</td>
                  <td className="py-4 px-4 text-[#94A3B8]">1</td>
                  <td className="py-4 px-4 text-[#94A3B8]">Trial / Evaluation</td>
                </tr>
                <tr className="border-b border-[#2563EB]/10">
                  <td className="py-4 px-4 text-[#94A3B8]">Starter</td>
                  <td className="py-4 px-4 text-[#F8FAFC] font-semibold">$99/mo</td>
                  <td className="py-4 px-4 text-[#94A3B8]">500</td>
                  <td className="py-4 px-4 text-[#94A3B8]">1</td>
                  <td className="py-4 px-4 text-[#94A3B8]">Small e-commerce</td>
                </tr>
                <tr className="border-b border-[#2563EB]/10 bg-[#2563EB]/5">
                  <td className="py-4 px-4 text-[#2563EB] font-bold">Growth ⭐</td>
                  <td className="py-4 px-4 text-[#F8FAFC] font-semibold">$299/mo</td>
                  <td className="py-4 px-4 text-[#94A3B8]">2,500</td>
                  <td className="py-4 px-4 text-[#94A3B8]">2</td>
                  <td className="py-4 px-4 text-[#94A3B8]">Most popular</td>
                </tr>
                <tr className="border-b border-[#2563EB]/10">
                  <td className="py-4 px-4 text-[#94A3B8]">Scale</td>
                  <td className="py-4 px-4 text-[#F8FAFC] font-semibold">$799/mo</td>
                  <td className="py-4 px-4 text-[#94A3B8]">10,000</td>
                  <td className="py-4 px-4 text-[#94A3B8]">3</td>
                  <td className="py-4 px-4 text-[#94A3B8]">Multi-location retail</td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-[#94A3B8]">Enterprise</td>
                  <td className="py-4 px-4 text-[#F8FAFC] font-semibold">Custom</td>
                  <td className="py-4 px-4 text-[#94A3B8]">Unlimited</td>
                  <td className="py-4 px-4 text-[#94A3B8]">Unlimited</td>
                  <td className="py-4 px-4 text-[#94A3B8]">Large organizations</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827] no-print">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-[#2563EB]/20 to-[#111827] border border-[#2563EB]/40 rounded-3xl p-10">
            <h2 className="text-3xl font-bold text-[#F8FAFC] mb-4">Need More Resources?</h2>
            <p className="text-[#94A3B8] mb-8">
              Access our full sales toolkit, case studies, and demo environment.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/case-studies">
                <Button className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6">
                  View Case Studies
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing-check">
                <Button variant="outline" className="border-[#2563EB]/50 text-[#F8FAFC] hover:bg-[#2563EB]/10 px-8 py-6">
                  Try Demo Tool
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StrategyPage;
